import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { JivuCommandHandoffService } from '../services/jivu-command-handoff.service';

@Component({
  selector: 'app-jivu-command-handoff-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="jivu-command-dialog-backdrop" *ngIf="handoff.draft() as draft" role="dialog" aria-modal="true" aria-label="Move to Jivu Command">
      <section class="jivu-command-dialog" (click)="$event.stopPropagation()">
        <header class="jivu-command-dialog__header">
          <div>
            <span class="section-kicker">Jivu Command</span>
            <h3>Move this work to a device</h3>
          </div>
          <button type="button" class="icon-btn" (click)="handoff.close()" aria-label="Close">
            <i class="fas fa-times"></i>
          </button>
        </header>

        <div class="jivu-command-dialog__body">
          <div class="jivu-command-dialog__summary">
            <i class="fas fa-wand-magic-sparkles" aria-hidden="true"></i>
            <div>
              <strong>{{ draft.title }}</strong>
              <span>{{ draft.commandType.replaceAll('_', ' ') }}</span>
            </div>
          </div>

          <div class="jivu-command-dialog__setup" *ngIf="handoff.setupRequired()">
            <i class="fas fa-database"></i>
            <span>{{ handoff.setupMessage() || 'Jivu Command database setup is required.' }}</span>
          </div>

          <label class="jivu-command-dialog__field">
            <span>Target device</span>
            <div class="jivu-command-dialog__select">
              <i class="fas fa-display" aria-hidden="true"></i>
              <select class="modal-input" [ngModel]="handoff.selectedDeviceId()" (ngModelChange)="handoff.selectedDeviceId.set($event)">
                <option value="">Select registered device</option>
                <option *ngFor="let device of handoff.targetDevices()" [value]="device.deviceInstanceId">
                  {{ handoff.deviceLabel(device) }}
                </option>
              </select>
              <i class="fas fa-chevron-down" aria-hidden="true"></i>
            </div>
          </label>

          <div class="jivu-command-dialog__empty" *ngIf="handoff.targetDevices().length === 0">
            Open Jivu on this or another device first. It will appear here after its first heartbeat.
          </div>

          <div class="jivu-command-dialog__active" *ngIf="handoff.selectedDeviceId()">
            <span>Selected device</span>
            <strong>{{ handoff.selectedDeviceStatus() }}</strong>
            <small>One active command runs per device. New work waits safely in queue.</small>
          </div>

          <p class="jivu-command-dialog__error" *ngIf="handoff.error()">{{ handoff.error() }}</p>
        </div>

        <footer class="jivu-command-dialog__actions">
          <button type="button" class="btn btn-soft" (click)="handoff.refreshDevices()" [disabled]="handoff.isSubmitting()">
            <i class="fas fa-rotate"></i>
            <span>Refresh</span>
          </button>
          <button type="button" class="btn btn-primary" (click)="handoff.send()" [disabled]="handoff.isSubmitting() || handoff.setupRequired()">
            <i class="fas" [ngClass]="handoff.isSubmitting() ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
            <span>{{ handoff.isSubmitting() ? 'Sending...' : 'Send Command' }}</span>
          </button>
        </footer>
      </section>
    </div>
  `,
  styles: [`
    .jivu-command-dialog-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1200;
      display: grid;
      place-items: center;
      padding: 1rem;
      background: rgba(7, 10, 18, 0.68);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
    }

    .jivu-command-dialog {
      width: min(560px, 100%);
      border: 1px solid var(--border, rgba(255, 255, 255, 0.14));
      border-radius: 8px;
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--workspace-surface-strong, #111827) 94%, white 6%), var(--workspace-surface-strong, #101522));
      color: var(--text, #f8fafc);
      box-shadow: 0 26px 90px rgba(0, 0, 0, 0.44);
      overflow: hidden;
    }

    .jivu-command-dialog__header,
    .jivu-command-dialog__actions {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
      padding: 18px 18px 14px;
      border-bottom: 1px solid var(--border, rgba(255, 255, 255, 0.12));
    }

    .jivu-command-dialog__actions {
      border-top: 1px solid var(--border, rgba(255, 255, 255, 0.12));
      border-bottom: 0;
      justify-content: flex-end;
      padding: 14px 18px 18px;
      background: color-mix(in srgb, var(--workspace-surface-strong, #101522) 88%, black 12%);
    }

    .jivu-command-dialog__header h3 {
      margin: 0.2rem 0 0;
      color: var(--text, #f8fafc);
      font-size: 1.08rem;
      line-height: 1.25;
    }

    .section-kicker {
      color: var(--accent, #d6b95e);
      font-size: 0.72rem;
      font-weight: 800;
      letter-spacing: 0;
      text-transform: uppercase;
    }

    .icon-btn {
      width: 36px;
      height: 36px;
      display: grid;
      place-items: center;
      border: 1px solid transparent;
      border-radius: 8px;
      color: var(--text2, #cbd5e1);
      background: transparent;
      cursor: pointer;
    }

    .icon-btn:hover {
      border-color: var(--border, rgba(255, 255, 255, 0.14));
      background: rgba(255, 255, 255, 0.07);
      color: var(--text, #f8fafc);
    }

    .jivu-command-dialog__body {
      display: grid;
      gap: 0.9rem;
      padding: 18px;
    }

    .jivu-command-dialog__summary,
    .jivu-command-dialog__active,
    .jivu-command-dialog__setup {
      display: grid;
      gap: 0.35rem;
      padding: 14px;
      border: 1px solid var(--border, rgba(255, 255, 255, 0.12));
      border-radius: 8px;
      background: color-mix(in srgb, var(--workspace-surface, #151b2a) 88%, transparent);
    }

    .jivu-command-dialog__summary {
      grid-template-columns: 38px 1fr;
      align-items: center;
    }

    .jivu-command-dialog__summary > i {
      width: 38px;
      height: 38px;
      display: grid;
      place-items: center;
      border-radius: 8px;
      color: var(--accent-contrast, #111827);
      background: var(--accent, #d6b95e);
    }

    .jivu-command-dialog__summary strong,
    .jivu-command-dialog__active strong {
      color: var(--text, #f8fafc);
    }

    .jivu-command-dialog__setup {
      grid-template-columns: auto 1fr;
      align-items: center;
      color: var(--warning, #f4c84a);
    }

    .jivu-command-dialog__summary span,
    .jivu-command-dialog__active span,
    .jivu-command-dialog__active small,
    .jivu-command-dialog__empty {
      color: var(--text2, #cbd5e1);
      font-size: 0.85rem;
    }

    .jivu-command-dialog__field {
      display: grid;
      gap: 0.4rem;
    }

    .jivu-command-dialog__field span {
      font-weight: 700;
      font-size: 0.85rem;
      color: var(--text, #f8fafc);
    }

    .jivu-command-dialog__select {
      display: grid;
      grid-template-columns: auto 1fr auto;
      align-items: center;
      gap: 10px;
      min-height: 46px;
      padding: 0 12px;
      border: 1px solid var(--border, rgba(255, 255, 255, 0.14));
      border-radius: 8px;
      background: color-mix(in srgb, var(--workspace-surface, #151b2a) 92%, white 8%);
      color: var(--text2, #cbd5e1);
    }

    .jivu-command-dialog__select:focus-within {
      border-color: color-mix(in srgb, var(--accent, #d6b95e) 62%, transparent);
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent, #d6b95e) 18%, transparent);
    }

    .jivu-command-dialog__select select {
      width: 100%;
      min-width: 0;
      border: 0;
      outline: 0;
      color: var(--text, #f8fafc);
      background: transparent;
      font-weight: 700;
      appearance: none;
    }

    .jivu-command-dialog__error {
      margin: 0;
      color: var(--danger, #f87171);
      font-weight: 700;
      font-size: 0.9rem;
    }

    .btn {
      min-height: 42px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 0 16px;
      border-radius: 8px;
      border: 1px solid var(--border, rgba(255, 255, 255, 0.14));
      font-weight: 800;
      cursor: pointer;
    }

    .btn-soft {
      color: var(--text2, #cbd5e1);
      background: rgba(255, 255, 255, 0.07);
    }

    .btn-soft:hover {
      color: var(--text, #f8fafc);
      background: rgba(255, 255, 255, 0.11);
    }

    .btn-primary {
      border-color: transparent;
      color: var(--accent-contrast, #111827);
      background: var(--accent, #d6b95e);
    }

    .btn:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }
  `]
})
export class JivuCommandHandoffDialogComponent {
  readonly handoff = inject(JivuCommandHandoffService);
}
